/**
 * AI explanation template — Phase 4.3.
 *
 * Doc: tm-ai-workspace/plan/funpaper-app/10-implementation-phases.md §8.4
 *
 * Pure function — no I/O, no React, no API calls. Generates a
 * natural-language explanation of why this worksheet matches the child,
 * using simple template substitution. No LLM Y1 (deferred to Y2).
 *
 * Y1 simplified:
 *  - tema is the first item from worksheet.temaNames (or fallback)
 *  - familiarity is "suka" if any tema is in child.preferredTema, else "baru kenal"
 *  - skills are hardcoded (no skills_json in catalog-dev)
 *  - duration defaults to 8 minutes if null
 *
 * Example output:
 *   "Aisyah usia 4, suka Tanaman. Worksheet ini melatih koordinasi
 *    tangan-mata dan pengenalan bentuk. Estimasi 8 menit."
 */

export interface ExplanationChild {
  name: string;
  age: number;
  preferredTema: string[];
}

export interface ExplanationWorksheet {
  name: string;
  /** 14 PAUD-specific tema names (sorted by sort_order, may be empty). */
  temaNames: string[];
  /** Denormalized activity (e.g. "Mewarnai"). May be null. */
  activityLabel: string | null;
  /** Estimated duration in minutes. May be null. */
  durationMinutes: number | null;
  /** Optional override for tema (when temaNames is empty). */
  themeLabel?: string | null;
}

// ----------------------------------------------------------------------------
// Skill table — Y1 hardcoded by activity
// ----------------------------------------------------------------------------

/** Y1 skill mapping per activity. Picked the 2 most-relevant skills per type.
 *  Plan 21: diperluas ke 16 aktivitas kanonik; nama tanpa entri memakai
 *  DEFAULT_SKILLS (fallback, bukan error). */
const SKILL_BY_ACTIVITY: Record<string, [string, string]> = {
  Mewarnai: ['koordinasi tangan-mata', 'pengenalan bentuk'],
  'Menebalkan Garis': ['motorik halus', 'konsentrasi'],
  Maze: ['pemecahan masalah', 'motorik halus'],
  Mencocokkan: ['pengenalan pola', 'konsentrasi'],
  Berhitung: ['pengenalan angka', 'logika sederhana'],
  'Mengenal Huruf': ['literasi awal', 'pengenalan suara'],
  'Gunting Tempel': ['motorik halus', 'kreativitas'],
  'Menghubungkan Titik': ['motorik halus', 'pengenalan bentuk'],
  Menggunting: ['motorik halus', 'koordinasi tangan-mata'],
  'Melingkari Gambar': ['pengenalan bentuk', 'konsentrasi'],
  'Soal Campuran': ['logika sederhana', 'pemecahan masalah'],
  Analisa: ['berpikir kritis', 'pengenalan pola'],
  Membandingkan: ['berpikir logis', 'pengenalan ukuran'],
  'Art and Craft': ['kreativitas', 'motorik halus'],
  Doa: ['kebiasaan baik', 'motorik halus'],
  'Menulis dan Menggambar': ['motorik halus', 'kreativitas'],
  'Operasi Hitung': ['pengenalan angka', 'logika sederhana'],
  Menulis: ['motorik halus', 'literasi awal'],
};

/** Default skills when activity doesn't match the table. */
const DEFAULT_SKILLS: [string, string] = ['motorik halus', 'konsentrasi'];

// ----------------------------------------------------------------------------
// Public API
// ----------------------------------------------------------------------------

/**
 * Generate a natural-language explanation for a worksheet relative to a
 * child. Returns a single sentence (or 3 short sentences) in Bahasa
 * Indonesia suitable for the Worksheet Detail screen.
 *
 * @param child     Child profile (name, age, preferredTema)
 * @param worksheet Worksheet metadata (name, temaNames, activity, duration)
 * @returns Explanation string in BI
 */
export function generateExplanation(
  child: ExplanationChild,
  worksheet: ExplanationWorksheet,
): string {
  const tema = pickTema(worksheet);
  const familiarity = pickFamiliarity(tema, child.preferredTema);
  const skills = pickSkills(worksheet.activityLabel);
  const duration = pickDuration(worksheet.durationMinutes);

  return (
    `${child.name} usia ${child.age}, ${familiarity} ${tema}. ` +
    `Worksheet ini melatih ${skills[0]} dan ${skills[1]}. ` +
    `Estimasi ${duration} menit.`
  );
}

// ----------------------------------------------------------------------------
// Internal helpers (exported for testability)
// ----------------------------------------------------------------------------

export function pickTema(worksheet: ExplanationWorksheet): string {
  if (worksheet.temaNames.length > 0) {
    return worksheet.temaNames[0]!;
  }
  if (worksheet.themeLabel) return worksheet.themeLabel;
  return 'beragam tema';
}

export function pickFamiliarity(
  tema: string,
  preferredTema: string[],
): 'suka' | 'baru kenal' {
  if (preferredTema.length === 0) return 'baru kenal';
  return preferredTema.includes(tema) ? 'suka' : 'baru kenal';
}

export function pickSkills(
  activityLabel: string | null,
): [string, string] {
  if (!activityLabel) return DEFAULT_SKILLS;
  return SKILL_BY_ACTIVITY[activityLabel] ?? DEFAULT_SKILLS;
}

export function pickDuration(durationMinutes: number | null): number {
  if (durationMinutes && durationMinutes > 0) return durationMinutes;
  return 8;
}
